import { dedupeConcurrentRequest } from "@/app/_utils/dedupeConcurrentRequest";
import { getCsrfToken } from "@/app/_utils/csrf";

export type Performer = {
  id: string;
  img_url?: string | null;
  first_name: string;
  last_name: string;
  full_name?: string;
  instagram?: string | null;
  email?: string | null;
  phone?: string | null;
  shows_count?: number | null;
  created_at: string;
  updated_at: string;
};

export type PerformerShowRow = {
  id: string;
  title: string;
  start: string;
  img_url?: string | null;
  location_id?: string | null;
  location_name?: string | null;
};

class PerformerService {
  static instance: PerformerService;
  csrfToken = getCsrfToken();
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;
  private readonly inflight = new Map<string, Promise<unknown>>();

  getPerformerAsync = async (id: string): Promise<Performer> => {
    const res = await fetch(`${this.baseUrl}performers/${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getPerformersAsync = async (): Promise<Performer[]> => {
    return dedupeConcurrentRequest(this.inflight, "list", async () => {
      const res = await fetch(`${this.baseUrl}performers/`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      return await res.json();
    }) as Promise<Performer[]>;
  };

  getPerformerShowsAsync = async (id: string): Promise<PerformerShowRow[]> => {
    const res = await fetch(`${this.baseUrl}performers/${id}/shows/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  createPerformerAsync = async (performer: Partial<Performer> & { image?: string }) => {
    const res = await fetch(`${this.baseUrl}performers/`, {
      method: "POST",
      body: JSON.stringify(performer),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return await res.json();
  };

  updatePerformerAsync = async (id: string, performer: Partial<Performer> & { image?: string }) => {
    const res = await fetch(`${this.baseUrl}performers/${id}/`, {
      method: "PUT",
      body: JSON.stringify(performer),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return await res.json();
  };

  deletePerformerAsync = async (id: string) => {
    return await fetch(`${this.baseUrl}performers/${id}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": this.csrfToken },
      credentials: "include",
    });
  };
}

export default new PerformerService();
